import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/services/api';
import { useTheme } from '@/hooks/useTheme';

interface SkoCommand {
  id: string;
  commandType: string;
  severity: string;
  assetId: string | null;
  assetName: string | null;
  receivedVia: string;
  dispatcherName: string | null;
  receivedAt: string;
  effectiveFrom: string | null;
  effectiveUntil: string | null;
  ackDeadline: string | null;
  ackMinutesLeft: number | null;
  minutesRemaining: number | null;
  curtailmentPct: number | null;
  targetKw: number | null;
  reason: string | null;
  status: string;
  actualCompliancePct: number | null;
}

interface InboxResponse {
  count: number;
  commands: SkoCommand[];
}

const fmtTime = (d: string | null): string => {
  if (!d) return '';
  return d.replace('T', ' ').slice(11, 16);
};
const fmtDate = (d: string | null): string => {
  if (!d) return '';
  return d.replace('T', ' ').slice(0, 16);
};

const STATUS_META: Record<string, { color: string; bg: string; icon: string; label: string }> = {
  pending:      { color: 'var(--warn)', bg: 'bg-[color-mix(in_srgb,var(--warn)_10%,transparent)]', icon: 'ri-error-warning-line',   label: 'PENDING' },
  acknowledged: { color: '#F59E0B', bg: 'bg-yellow-50',  icon: 'ri-check-line',            label: 'ACKED' },
  executing:    { color: '#F59E0B', bg: 'bg-yellow-50',  icon: 'ri-loader-4-line',         label: 'EXECUTING' },
  completed:    { color: '#10B981', bg: 'bg-green-50',   icon: 'ri-check-double-line',     label: 'COMPLETED' },
  expired:      { color: '#6B7280', bg: 'bg-gray-100',   icon: 'ri-time-line',             label: 'EXPIRED' },
  rejected:     { color: '#6B7280', bg: 'bg-gray-100',   icon: 'ri-close-circle-line',     label: 'REJECTED' },
  cancelled:    { color: '#6B7280', bg: 'bg-gray-100',   icon: 'ri-prohibited-line',       label: 'CANCELLED' },
};

export default function SkoInboxWidget() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [data, setData] = useState<InboxResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ackingId, setAckingId] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await api.get<InboxResponse>('/sko/commands/inbox?limit=10');
      setData(r);
      setError(null);
    } catch (e: any) {
      setError(e?.message ?? 'load failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60 * 1000); // refresh every minute
    return () => clearInterval(t);
  }, []);

  const handleAck = async (id: string) => {
    setAckingId(id);
    try {
      await api.post(`/sko/commands/${id}/ack`, { ackVia: 'web_ui' });
      await load();
    } catch (e: any) {
      setError(e?.message ?? 'ack failed');
    } finally {
      setAckingId(null);
    }
  };

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';

  return (
    <div className="rounded-xl overflow-hidden flex flex-col" style={{ background: cardBg, border: `1px solid ${cardBorder}`, height: '100%' }}>
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${cardBorder}` }}>
        <div className="flex items-center gap-2">
          <i className="ri-inbox-line text-base" style={{ color: '#0066FF' }}></i>
          <div>
            <h3 className="font-semibold text-sm" style={{ color: textMain }}>
              {t('sko_inbox_title')}
            </h3>
            <div className="text-[10px]" style={{ color: textMuted }}>{t('sko_inbox_subtitle')}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/sko')}
          className="text-xs hover:underline cursor-pointer whitespace-nowrap"
          style={{ color: '#0066FF' }}
        >
          {t('sko_inbox_view_all')} →
        </button>
      </div>

      <div className="flex-1 overflow-y-auto" style={{ maxHeight: '400px' }}>
        {loading && <div className="p-4 text-xs" style={{ color: textMuted }}>{t('sko_inbox_loading')}</div>}
        {error && <div className="p-3 text-xs text-[var(--err)]">{error}</div>}
        {data && data.count === 0 && (
          <div className="p-6 text-center text-xs" style={{ color: textMuted }}>
            <i className="ri-mail-check-line text-2xl block mb-1"></i>
            {t('sko_inbox_empty')}
          </div>
        )}
        {data?.commands.map((c) => {
          const meta = STATUS_META[c.status] || STATUS_META.pending;
          const isPending = c.status === 'pending';
          return (
            <div key={c.id} style={{ borderBottom: `1px solid ${cardBorder}` }} className="px-3 py-2">
              <div className="flex items-start gap-2">
                <i className={`${meta.icon} mt-0.5`} style={{ color: meta.color }}></i>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                      style={{ background: meta.color, color: 'white' }}>{meta.label}</span>
                    {c.severity === 'critical' && (
                      <span className="text-[10px] font-semibold text-[var(--err)] uppercase">!!! critical</span>
                    )}
                    <span className="text-xs font-medium" style={{ color: textMain }}>
                      {c.assetName ?? t('sko_inbox_no_asset')}
                    </span>
                    {c.commandType !== 'CURTAILMENT' && (
                      <span className="text-[10px]" style={{ color: textMuted }}>[{c.commandType}]</span>
                    )}
                  </div>

                  <div className="text-xs mt-1" style={{ color: textMain }}>
                    {c.curtailmentPct !== null && (
                      <span className="font-semibold">{t('sko_inbox_curtailment_to')} {(100 - c.curtailmentPct).toFixed(0)}%
                        {c.targetKw !== null && <> (≤{c.targetKw.toFixed(0)} kW)</>}
                      </span>
                    )}
                    {c.effectiveFrom && (
                      <>  · {fmtTime(c.effectiveFrom)}–{fmtTime(c.effectiveUntil)}</>
                    )}
                  </div>

                  {c.reason && (
                    <div className="text-[10px] mt-0.5 truncate" style={{ color: textMuted }} title={c.reason}>
                      "{c.reason}"
                    </div>
                  )}

                  {isPending && c.ackMinutesLeft !== null && (
                    <div className="mt-1 text-[10px]">
                      <span style={{ color: c.ackMinutesLeft < 2 ? '#EF4444' : '#F59E0B' }}>
                        ⏱ {t('sko_inbox_ack_in')} {Math.max(0, Math.ceil(c.ackMinutesLeft))} {t('sko_inbox_min')}
                      </span>
                    </div>
                  )}

                  {c.status === 'completed' && c.actualCompliancePct !== null && (
                    <div className="mt-1 text-[10px]" style={{ color: textMuted }}>
                      Compliance: <span className="font-semibold" style={{ color: c.actualCompliancePct >= 95 ? '#10B981' : '#F59E0B' }}>
                        {c.actualCompliancePct.toFixed(1)}%
                      </span>
                    </div>
                  )}

                  {isPending && (
                    <div className="mt-2 flex gap-1.5">
                      <button
                        type="button"
                        disabled={ackingId === c.id}
                        onClick={() => handleAck(c.id)}
                        className="text-[11px] px-2 py-1 rounded cursor-pointer transition disabled:opacity-50 whitespace-nowrap"
                        style={{ background: '#10B981', color: 'white' }}
                      >
                        {ackingId === c.id ? '...' : `✓ ${t('sko_inbox_ack')}`}
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate(`/sko?id=${c.id}`)}
                        className="text-[11px] px-2 py-1 rounded cursor-pointer transition border whitespace-nowrap"
                        style={{ color: textMain, borderColor: cardBorder }}
                      >
                        {t('sko_inbox_details')}
                      </button>
                    </div>
                  )}
                </div>
                <div className="text-[10px] shrink-0" style={{ color: textMuted }}>{fmtTime(c.receivedAt)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}